import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_LOGIN } from "../../route";
import {
  COOKIE_ACCESS_MAIL,
  COOKIE_ACCESS_ROLE,
} from "../../utils/storage-constants";
import {
  API_SIGN_UP,
  API_GET_BOARD,
  API_GET_CLASS_GRADE,
  API_GET_EDUCTION_LEVEL,
} from "../../utils/api-routes";
import { useNotify } from "../../hooks/use-notify";

const SCHOOL_LEVELS = ["Primary", "Secondary", "Higher Secondary"];

const DEFAULT_ERROR = {
  errEmail: "",
  errPhone: "",
  errName: "",
  errFocus: "",
  errCity: "",
  errLevel: "",
  errGradeClass: "",
  errBoard: "",
  isClicked: false,
  isValid: false,
};

const useHandler = () => {
  const { get, post } = useApi();
  const { updateRole, updateMail } = useSingleton();
  const { toggleNotify } = useNotify();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const isEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

  const [data, setData] = useState({
    email: "",
    name: "",
    phone: "",
    focus: [],
    city: "",
    level: "",
    grade: "",
    board: "",
  });
  const [error, setError] = useState(DEFAULT_ERROR);
  const [eductationLevel, setEducationLevel] = useState([]);
  const [gradeClass, setGradeClass] = useState([]);
  const [boards, setBoards] = useState([]);

  const { email, name, phone, focus, city, level, grade, board } = data;
  const { isClicked } = error;

  const isSchool = SCHOOL_LEVELS.includes(level);

  // Static grade ranges per level — avoids dependency on API string format
  const GRADE_MAP = {
    Primary: ["1", "2", "3", "4", "5"],
    Secondary: ["6", "7", "8", "9", "10"],
    "Higher Secondary": ["11", "12"],
  };
  const filteredGradeClass = GRADE_MAP[level] ?? gradeClass ?? [];


  const onChangeValue = (e) => {
    const { name, value } = e.target;
    // Reset grade when level changes
    setData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "level" ? { grade: "" } : {}),
    }));
    setError((prev) => ({
      ...prev,
      [`err${name.charAt(0).toUpperCase() + name.slice(1)}`]: "",
      isValid: true,
    }));
  };

  const onFocusChange = (item) => {
    setData((prev) => {
      const currentFocus = prev.focus;
      const updated = currentFocus.includes(item)
        ? currentFocus.filter((f) => f !== item)
        : [...currentFocus, item];
      return { ...prev, focus: updated };
    });
    setError((prev) => ({ ...prev, errFocus: "", isValid: true }));
  };

  const invalidInputRegex = (input, regex) => new RegExp(regex).test(input);

  const toggleCheck = () => {
    setData((prev) => ({ ...prev, check: !prev.check }));
  };

  const onValidate = (step) => {
    let errState = { ...DEFAULT_ERROR, isValid: true, isClicked: true };

    if (step === 1) {
      if (!name) {
        errState.errName = "Please Enter Name";
        errState.isValid = false;
      }
      if (!invalidInputRegex(email, isEmail)) {
        errState.errEmail = "Please enter valid email";
        errState.isValid = false;
      }
      if (!phone) {
        errState.errPhone = "Please Enter Your PhoneNumber";
        errState.isValid = false;
      }
    }

    if (step === 2) {
      if (!level) {
        errState.errLevel = "Please select your educational level";
        errState.isValid = false;
      }
      if (isSchool) {
        if (!grade) {
          errState.errGradeClass = "Please select your grade";
          errState.isValid = false;
        }
        if (!board) {
          errState.errBoard = "Please select your board";
          errState.isValid = false;
        }
      }
      if (!focus || focus.length === 0) {
        errState.errFocus = "Please select at least one focus";
        errState.isValid = false;
      }
    }

    if (step === 3) {
      if (!city) {
        errState.errCity = "Please Enter Your City";
        errState.isValid = false;
      }
    }

    setError(errState);
    return errState;
  };

  const getEducationLevel = async () => {
    try {
      const res = await get(API_GET_EDUCTION_LEVEL);
      if (res) setEducationLevel(res?.data?.education_level || []);
    } catch (e) {
      console.log("ERROR COMES FROM API EDUCATION LEVEL", e);
    }
  };

  const getClassAndGrade = async () => {
    try {
      const res = await get(API_GET_CLASS_GRADE);
      if (res) setGradeClass(res?.data?.class_grade || []);
    } catch (e) {
      console.log("ERROR COMES FROM API CLASS AND GRADE", e);
    }
  };

  const getBoards = async () => {
    try {
      const res = await get(API_GET_BOARD);
      if (res) setBoards(res?.data?.board || []);
    } catch (e) {
      console.log("ERROR COMES FROM API BOARD", e);
    }
  };

  useEffect(() => { getEducationLevel(); }, []);
  useEffect(() => { getClassAndGrade(); }, []);
  useEffect(() => { getBoards(); }, []);

  const onSubmit = async () => {
    const { isValid } = onValidate();

    if (!isValid) return;
    setIsLoading(true);
    try {
      const payload = {
        email: email.trim(),
        phone,
        name,
        focus,
        city,
        level,
      };
      if (isSchool) {
        payload.class_grade = grade;
        payload.board = board;
      }
      const response = await post(API_SIGN_UP, payload);

      const apiData = response?.data;
      if (!apiData?.role) {
        toggleNotify({
          type: "error",
          title: "Signup Failed",
          description: apiData?.message || "This email may already be registered.",
        });
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      // Clear any stale error toast before navigating
      toggleNotify(null);
      navigate(ROUTE_LOGIN, { state: { signupSuccess: true } });
    } catch (error) {
      console.error("Signup error:", error);
      setIsLoading(false);
      const errMsg = error?.response?.data?.message || "An error occurred. Please try again.";
      toggleNotify({
        type: "error",
        title: "Signup Failed",
        description: errMsg,
      });
    }
  };

  return {
    data,
    error,
    isLoading,
    eductationLevel,
    gradeClass: filteredGradeClass,
    boards,
    isSchool,
    onSubmit,
    onChangeValue,
    onFocusChange,
    toggleCheck,
    onValidate,
  };
};

export default useHandler;
